import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Shield, Clock, MapPin, Star, Phone, ChevronRight,
  Car, Wrench, Film, Settings, CheckCircle
} from "lucide-react";
import { Button } from "../components/ui/button";

const HERO_IMAGE = "https://images.unsplash.com/photo-1672870987157-9715da8c4a84?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const services = [
  {
    icon: Car,
    title: "Remplacement de pare-brise",
    description: "Intervention rapide à domicile avec des vitrages de qualité OEM.",
  },
  {
    icon: Wrench,
    title: "Réparation de vitrage",
    description: "Réparation d'impacts pour éviter la propagation des fissures.",
  },
  {
    icon: Film,
    title: "Films teintés",
    description: "Pose professionnelle de films solaires homologués.",
  },
  {
    icon: Settings,
    title: "Calibrage ADAS",
    description: "Calibrage précis des systèmes d'aide à la conduite.",
  },
];

const advantages = [
  { icon: Clock, title: "Intervention rapide", text: "Sous 24-48h à domicile" },
  { icon: Shield, title: "Qualité garantie", text: "Vitrages certifiés OEM" },
  { icon: MapPin, title: "À domicile", text: "On vient à vous" },
  { icon: CheckCircle, title: "Prise en charge", text: "Assurance & tiers payant" },
];

const reviews = [
  {
    name: "Marie L.",
    location: "Metz",
    rating: 5,
    text: "Service impeccable ! Le technicien est venu chez moi le lendemain de mon appel. Travail soigné et professionnel.",
  },
  {
    name: "Thomas D.",
    location: "Nancy",
    rating: 5,
    text: "Très satisfait du remplacement de mon pare-brise. Rapport qualité-prix excellent et équipe très réactive.",
  },
  {
    name: "Sophie M.",
    location: "Thionville",
    rating: 5,
    text: "Calibrage ADAS effectué avec professionnalisme. Je recommande vivement MY VITREO !",
  },
];

const zones = [
  "Metz", "Thionville", "Nancy", "Forbach", 
  "Sarreguemines", "Amnéville", "Épinal"
];

export default function HomePage() {
  return (
    <div className="pt-20 sm:pt-24 lg:pt-28" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent" />
        </div>
        
        {/* Hero Glow Effect */}
        <div className="absolute inset-0 hero-gradient pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-sky-500/20 text-sky-400 rounded-full text-sm font-medium mb-6">
              Service à domicile en Lorraine & Grand Est
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Remplacement de pare-brise à domicile à{" "}
              <span className="text-sky-400">Metz</span>, en Lorraine et dans le Grand Est
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed">
              MY VITREO – La solution vitrage de confiance. Service professionnel, 
              rapide et garanti pour tous vos besoins en vitrage automobile.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/rendez-vous">
                <Button 
                  className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-sky-500/30"
                  data-testid="hero-cta-rdv"
                >
                  Prendre rendez-vous
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/devis">
                <Button 
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold"
                  data-testid="hero-cta-devis"
                >
                  Demander un devis gratuit
                </Button>
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-6 mt-10 pt-10 border-t border-white/10">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="text-white font-medium">5.0</span>
                <span className="text-slate-400">• 150+ avis</span>
              </div>
              <div className="h-6 w-px bg-white/20 hidden sm:block" />
              <span className="text-slate-300">
                <CheckCircle className="w-4 h-4 inline mr-1 text-green-400" />
                Agréé assurance
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-slate-900/50" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sky-400 font-medium uppercase tracking-wider text-sm">
              Nos services
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">
              Des solutions complètes pour votre vitrage
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8 rounded-3xl card-hover group"
              >
                <div className="w-14 h-14 bg-sky-500/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-sky-500 transition-colors duration-300">
                  <service.icon className="w-7 h-7 text-sky-400 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                variant="outline" 
                className="border-2 border-sky-500 text-sky-400 hover:bg-sky-500/20 rounded-full px-8 py-4"
              >
                Voir tous nos services
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding" data-testid="advantages-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-sky-400 font-medium uppercase tracking-wider text-sm">
                Pourquoi nous choisir
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-6">
                L'excellence du vitrage automobile à votre service
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Avec MY VITREO, bénéficiez d'un service professionnel et personnalisé. 
                Nous intervenons directement chez vous ou sur votre lieu de travail, 
                avec des techniciens qualifiés et des vitrages de qualité constructeur.
              </p>

              <div className="grid grid-cols-2 gap-6">
                {advantages.map((adv, index) => (
                  <motion.div
                    key={adv.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-12 h-12 bg-sky-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <adv.icon className="w-6 h-6 text-sky-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{adv.title}</h4>
                      <p className="text-slate-400 text-sm">{adv.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="glass-card p-8 rounded-3xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_my-vitreo-preview/artifacts/q2nk5zyo_IMG_8929.jpeg"
                  alt="Technicien MY VITREO en intervention"
                  className="rounded-2xl w-full h-[400px] object-cover"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 glass-card p-4 rounded-2xl animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">+500</p>
                    <p className="text-sm text-slate-400">Interventions/an</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section-padding bg-slate-900" data-testid="zones-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-sky-400 font-medium uppercase tracking-wider text-sm">
              Zone d'intervention
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">
              Nous intervenons dans toute la Lorraine
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-slate-300 text-lg mb-8">
                Basé à Metz, MY VITREO intervient dans toute la région Lorraine et le Grand Est. 
                Nos techniciens se déplacent à votre domicile ou sur votre lieu de travail.
              </p>
              
              <h3 className="text-xl font-semibold text-white mb-4">Principales villes desservies :</h3>
              <div className="flex flex-wrap gap-3 mb-8">
                {zones.map((zone) => (
                  <span
                    key={zone}
                    className="px-4 py-2 bg-sky-500/20 text-sky-300 rounded-full text-sm font-medium"
                  >
                    {zone}
                  </span>
                ))}
              </div>

              <p className="text-slate-400 text-sm">
                Vous habitez ailleurs en Lorraine ou dans le Grand Est ? Contactez-nous pour vérifier 
                notre disponibilité dans votre secteur.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="map-container">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d330022.0477426665!2d6.0!3d49.1!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4794dc09d8d63e35%3A0x406f69c2f3d9f80!2sMetz!5e0!3m2!1sfr!2sfr!4v1710000000000"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Zone d'intervention MY VITREO"
                ></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="section-padding" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-sky-400 font-medium uppercase tracking-wider text-sm">
              Témoignages
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">
              Ce que disent nos clients
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <motion.div
                key={review.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8 rounded-3xl"
              >
                <div className="flex mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-300 mb-6 leading-relaxed">"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-sky-500/20 rounded-full flex items-center justify-center">
                    <span className="text-sky-400 font-semibold">{review.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">{review.name}</p>
                    <p className="text-sm text-slate-400">{review.location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-sky-500" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Besoin d'un remplacement de pare-brise ?
            </h2>
            <p className="text-sky-100 text-lg mb-8">
              Contactez-nous dès maintenant pour un devis gratuit ou prenez rendez-vous en ligne.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/rendez-vous">
                <Button className="w-full sm:w-auto bg-white text-sky-600 hover:bg-sky-50 rounded-full px-8 py-6 text-lg font-semibold">
                  Prendre rendez-vous
                </Button>
              </Link>
              <a href="tel:0695442788">
                <Button variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold">
                  <Phone className="mr-2 w-5 h-5" />
                  06 95 44 27 88
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
